/**
 * Alliance 목록·초대 후보 등 — 비교·매핑은 영문 코드, 화면 문구는 별도 라벨.
 *
 * 상태 축(서로 다른 DB/도메인 — 섞어 쓰지 않음):
 * - 등록·승인: GLOW_ALLIANCE_SPACE.APPROVAL_STATUS_CD (`ALLIANCE_APPROVAL_STATUS`).
 *   포털 정책 `allianceSpaceRequireAdminApproval` 이 켜지면 생성 시 PENDING → 관리자 승인 후 APPROVED.
 * - 생명주기 단계: STAGE_CD (`ALLIANCE_STAGE_CD`) — 사전 협의 PRE vs 정식 ALLIANCE.
 * - 운영 표시: OPERATION_STATUS_CD (`ALLIANCE_OPERATION_STATUS`) — 방장이 운영/일시중지/종료.
 * - 아래 `ALLIANCE_MOCK_*`: 정책·DB와 무관한 정적 목업 JSON(allianceApi mapCouncilToAllianceCard) 전용.
 */

/**
 * 목록 카드 필터용 생명주기 코드.
 * ENDED: GLOW_ALLIANCE_SPACE.END_AT 기준(프론트 mapBackendAllianceCard에서 계산).
 * CLOSED: 목록·필터용(운영 종료와 동일 코드 문자열일 수 있으나 출처는 별도 필드 조합).
 */
export const ALLIANCE_LIST_LIFECYCLE = Object.freeze({
  ENDED: 'ENDED',
  CLOSED: 'CLOSED',
});

/** GLOW_ALLIANCE_SPACE.APPROVAL_STATUS_CD (실데이터 API) */
export const ALLIANCE_APPROVAL_STATUS = Object.freeze({
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
});

/** GLOW_ALLIANCE_SPACE.STAGE_CD — Pre-Alliance vs 정식 Alliance */
export const ALLIANCE_STAGE_CD = Object.freeze({
  PRE: 'PRE',
  ALLIANCE: 'ALLIANCE',
});

/**
 * 협의체 목업 JSON 전용 progress (status / progressStatusCd).
 * 관리자 승인(PENDING 등)과 무관 — 실서비스 카드 상태는 ALLIANCE_APPROVAL_STATUS + STAGE_CD + OPERATION_STATUS 로 본다.
 */
export const ALLIANCE_MOCK_COUNCIL_PROGRESS = Object.freeze({
  IN_PROGRESS: 'IN_PROGRESS',
  WAITING: 'WAITING',
  ENDED: 'ENDED',
  CLOSED: 'CLOSED',
});

/** 초대 후보 병합 시 표시용 플레이스홀더 — 비교는 이 상수만 사용 */
export const ALLIANCE_INVITE_NAME_MISSING_LABEL_KO = '(이름 없음)';

/** GLOW_ALLIANCE_SPACE.OPERATION_STATUS_CD — 방장이 대시보드에서 변경 */
export const ALLIANCE_OPERATION_STATUS = Object.freeze({
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  CLOSED: 'CLOSED',
});

const OPERATION_STATUS_I18N_KEY = Object.freeze({
  [ALLIANCE_OPERATION_STATUS.ACTIVE]: 'alli.status.operationActive',
  [ALLIANCE_OPERATION_STATUS.PAUSED]: 'alli.status.operationPaused',
  [ALLIANCE_OPERATION_STATUS.CLOSED]: 'alli.status.operationClosed',
});

/** CustomSelect용 (value = API 코드) — UI에서 getAllianceOperationStatusSelectOptions(t) 사용 */
export const ALLIANCE_OPERATION_STATUS_SELECT_OPTIONS = Object.freeze([
  { value: ALLIANCE_OPERATION_STATUS.ACTIVE, labelKey: OPERATION_STATUS_I18N_KEY[ALLIANCE_OPERATION_STATUS.ACTIVE] },
  { value: ALLIANCE_OPERATION_STATUS.PAUSED, labelKey: OPERATION_STATUS_I18N_KEY[ALLIANCE_OPERATION_STATUS.PAUSED] },
  { value: ALLIANCE_OPERATION_STATUS.CLOSED, labelKey: OPERATION_STATUS_I18N_KEY[ALLIANCE_OPERATION_STATUS.CLOSED] },
]);

export function getAllianceOperationStatusSelectOptions(t) {
  return ALLIANCE_OPERATION_STATUS_SELECT_OPTIONS.map(({ value, labelKey }) => ({
    value,
    label: t(labelKey),
  }));
}

export function allianceOperationStatusLabelI18n(t, statusCd) {
  const key = OPERATION_STATUS_I18N_KEY[normalizeAllianceOperationStatusCd(statusCd)];
  return key ? t(key) : String(statusCd ?? '');
}

/** API/레거시 응답 정규화 (미정의 시 운영으로 간주) */
export function normalizeAllianceOperationStatusCd(raw) {
  const u = String(raw || '').trim().toUpperCase();
  if (u === ALLIANCE_OPERATION_STATUS.PAUSED) return ALLIANCE_OPERATION_STATUS.PAUSED;
  if (u === ALLIANCE_OPERATION_STATUS.CLOSED) return ALLIANCE_OPERATION_STATUS.CLOSED;
  return ALLIANCE_OPERATION_STATUS.ACTIVE;
}

const ALLIANCE_JOIN_STATUS_I18N_KEY = Object.freeze({
  ACCEPTED: 'alli.status.joinAccepted',
  INVITED: 'alli.status.joinInvited',
  REJECTED: 'alli.status.joinRejected',
});

/** 멤버 joinStatusCd 화면 표시 (코드 미정의 시 원문 유지) */
export function formatAllianceJoinStatusLabel(joinStatusCd, t) {
  if (joinStatusCd == null || joinStatusCd === '') return '-';
  const key = String(joinStatusCd).toUpperCase();
  const i18nKey = ALLIANCE_JOIN_STATUS_I18N_KEY[key];
  if (i18nKey && typeof t === 'function') return t(i18nKey);
  return String(joinStatusCd);
}

/** AllianceListFilter 상태 필터 키 → i18n */
export const ALLIANCE_LIST_STATUS_FILTER_I18N = Object.freeze({
  all: 'alli.status.all',
  pre: 'alli.status.preAlliance',
  alliance: 'alli.status.formalAlliance',
  pending: 'alli.status.pending',
  approved: 'alli.status.approved',
  rejected: 'alli.status.rejected',
  operation_paused: 'alli.status.operationPaused',
  operation_closed: 'alli.status.operationClosed',
  admin_closed: 'alli.status.adminClosed',
});

export function getAllianceListStatusFilterOptions(t, { isAdmin = false } = {}) {
  const keys = Object.keys(ALLIANCE_LIST_STATUS_FILTER_I18N).filter(
    (k) => isAdmin || k !== 'admin_closed'
  );
  return keys.map((key) => ({ key, label: t(ALLIANCE_LIST_STATUS_FILTER_I18N[key]) }));
}
